import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTranslations } from "use-intl";

import { useConfiguration } from "~/contexts/configuration";
import { useFavouriteStore } from "~/contexts/favourite-store";
import { Coords } from "~/shared/coords.class";

import Button from "../common/button";
import StoresModal, { calculateDistance, defaultLocation } from "./stores-modal";

type Props = {
  buttonClasses?: string;
};

export default function PreferredStoreModalWrapper(props: Props) {
  const t = useTranslations();
  const { eva } = useConfiguration();
  const { preferredStoreId } = useFavouriteStore();

  const [openModal, setOpenModal] = useState(false);
  const [locationPos, setLocationPos] = useState<Coords>(defaultLocation);

  const shopsByProximityQuery = useQuery({
    queryKey: ["getStores", locationPos],
    keepPreviousData: true,
    staleTime: Infinity,
    queryFn: async () => await eva.listShopsByProximity({ Latitude: locationPos.lat, Longitude: locationPos.lng }),
  });

  const storesFiltered = useMemo(() => {
    const stores = shopsByProximityQuery.data ?? [];

    // make sure stores with address and lat/lon are filtered
    const storesWithAddress = stores.filter(
      (store) => !!store.Address && (!!store.Address.Latitude || !!store.Latitude)
    );

    // we do not need to sort by locationPos since ListShopsByProximity handles it

    return storesWithAddress;
  }, [shopsByProximityQuery.data]);

  return (
    <>
      <Button className={props.buttonClasses} variant="secondary" onClick={() => setOpenModal(true)}>
        {t(preferredStoreId ? "generic.changestore" : "generic.selectpreferredstore")}
      </Button>
      <StoresModal
        open={openModal}
        onClose={() => {
          setLocationPos(defaultLocation);
          setOpenModal(false);
        }}
        locationPos={locationPos}
        setLocationPos={setLocationPos}
        stores={storesFiltered}
        isLoading={shopsByProximityQuery.isLoading}
      />
    </>
  );
}
